/**
 * MediaWorkspace - Full workspace gallery mode
 *
 * Replaces the center pane with a full-screen media gallery view.
 * Provides large grid, lightbox, detail panel, and "Load to Workspace" action.
 *
 * @module @humanizer/studio/components/workspace/MediaWorkspace
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../contexts/ApiContext';
import { usePanels } from '../../contexts/PanelContext';
import type { ApiMediaItem } from '../../types/media';

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export interface MediaWorkspaceProps {
  className?: string;
}

export function MediaWorkspace(): React.ReactElement {
  const api = useApi();
  const { deactivateMediaWorkspace } = usePanels();
  const [items, setItems] = useState<ApiMediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<ApiMediaItem | null>(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const PAGE_SIZE = 60;

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const result = await api.media.list({ limit: PAGE_SIZE, offset: page * PAGE_SIZE });
        setItems(result.items);
        setTotal(result.total);
      } catch {
        // Silently fail — items will be empty
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [api, page]);

  const handleClose = useCallback(() => {
    deactivateMediaWorkspace();
  }, [deactivateMediaWorkspace]);

  const handleItemClick = useCallback((item: ApiMediaItem) => {
    setSelectedItem(item);
  }, []);

  const handleCloseLightbox = useCallback(() => {
    setSelectedItem(null);
  }, []);

  const getMediaTypeFromMime = (mime: string): string => {
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('audio/')) return 'audio';
    if (mime.startsWith('video/')) return 'video';
    return 'other';
  };

  const formatSize = (bytes: number | null): string => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="media-workspace">
      {/* Header */}
      <div className="media-workspace__header">
        <h2 className="media-workspace__title">Media Gallery</h2>
        <div className="media-workspace__header-actions">
          <span className="media-workspace__count">{total} items</span>
          <button
            className="media-workspace__close-btn"
            onClick={handleClose}
            title="Return to workspace"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="media-workspace__loading">Loading media...</div>
      )}

      {/* Grid */}
      {!loading && (
        <div className="media-workspace__grid">
          {items.map((item) => {
            const mediaType = getMediaTypeFromMime(item.mimeType);
            return (
              <button
                key={item.id}
                className="media-workspace__card"
                onClick={() => handleItemClick(item)}
              >
                <div className="media-workspace__card-preview">
                  {mediaType === 'image' ? (
                    <div className="media-workspace__card-placeholder">
                      {item.filename?.split('.').pop()?.toUpperCase() || 'IMG'}
                    </div>
                  ) : (
                    <div className="media-workspace__card-icon">
                      {mediaType === 'audio' ? '🎵' : mediaType === 'video' ? '🎬' : '📄'}
                    </div>
                  )}
                </div>
                <div className="media-workspace__card-label">
                  {item.filename || item.contentHash.slice(0, 12)}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="media-workspace__pagination">
          <button
            className="media-workspace__page-btn"
            disabled={page === 0}
            onClick={() => setPage(p => p - 1)}
          >
            ← Prev
          </button>
          <span className="media-workspace__page-info">
            Page {page + 1} of {Math.ceil(total / PAGE_SIZE)}
          </span>
          <button
            className="media-workspace__page-btn"
            disabled={(page + 1) * PAGE_SIZE >= total}
            onClick={() => setPage(p => p + 1)}
          >
            Next →
          </button>
        </div>
      )}

      {/* Lightbox / Detail Panel */}
      {selectedItem && (
        <div className="media-workspace__lightbox" onClick={handleCloseLightbox} role="presentation">
          <div className="media-workspace__lightbox-content" onClick={(e) => e.stopPropagation()} role="presentation">
            <button
              className="media-workspace__lightbox-close"
              onClick={handleCloseLightbox}
            >
              ✕
            </button>

            <div className="media-workspace__lightbox-preview">
              {getMediaTypeFromMime(selectedItem.mimeType) === 'image' ? (
                <div className="media-workspace__lightbox-placeholder">
                  {selectedItem.filename?.split('.').pop()?.toUpperCase() || 'IMG'}
                </div>
              ) : (
                <div className="media-workspace__lightbox-icon">
                  {getMediaTypeFromMime(selectedItem.mimeType) === 'audio' ? '🎵' : '🎬'}
                </div>
              )}
            </div>

            <div className="media-workspace__detail">
              <h3 className="media-workspace__detail-filename">
                {selectedItem.filename || 'Untitled'}
              </h3>
              <dl className="media-workspace__detail-meta">
                <dt>Type</dt>
                <dd>{selectedItem.mimeType}</dd>
                <dt>Size</dt>
                <dd>{formatSize(selectedItem.size)}</dd>
                {selectedItem.width && selectedItem.height && (
                  <>
                    <dt>Dimensions</dt>
                    <dd>{selectedItem.width} × {selectedItem.height}</dd>
                  </>
                )}
                {selectedItem.sourceType && (
                  <>
                    <dt>Source</dt>
                    <dd>{selectedItem.sourceType}</dd>
                  </>
                )}
              </dl>
              {selectedItem.caption && (
                <div className="media-workspace__detail-section">
                  <h4>Caption</h4>
                  <p>{selectedItem.caption}</p>
                </div>
              )}
              {selectedItem.dallePrompt && (
                <div className="media-workspace__detail-section">
                  <h4>DALL-E Prompt</h4>
                  <p>{selectedItem.dallePrompt}</p>
                </div>
              )}
              {selectedItem.transcript && (
                <div className="media-workspace__detail-section">
                  <h4>Transcript</h4>
                  <p>{selectedItem.transcript}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MediaWorkspace;
